import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Share2, ImageDown } from "lucide-react";
import StageBadge, { TAPE_COLORS, TAPE_LABELS } from "../app/StageBadge";
import { track } from "../../lib/analytics";
import { saveVerdictCard } from "../../lib/verdictCard";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtD = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

const TONE_COLORS = { green: "#2BD97C", amber: "#E3A44A", red: "#C75E5E" };

const ShareRead = ({ symbol, line, mode = "stock", card }) => {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const url = `${window.location.origin}/api/share/${mode === "fund" ? "fund" : "late"}/${symbol}`;
  const share = async () => {
    track("check_shared", { symbol, mode }, true);
    if (navigator.share) {
      try { await navigator.share({ title: line, url }); return; } catch {}
    }
    try { await navigator.clipboard.writeText(url); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const saveImage = async () => {
    track("check_shared", { symbol, mode, channel: "image" }, true);
    await saveVerdictCard(card, mode);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };
  return (
    <div className="mt-2.5 pt-2.5 border-t border-[rgba(255,255,255,0.06)] flex flex-wrap items-center gap-4">
      <button
        type="button"
        onClick={share}
        data-testid="share-verdict-btn"
        className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#8B93A3] hover:text-[#E8EBF0] transition-colors"
      >
        <Share2 size={11} /> {copied ? "Link copied ✓" : "Share this read"}
      </button>
      {card && (
        <button
          type="button"
          onClick={saveImage}
          data-testid="share-verdict-image-btn"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#8B93A3] hover:text-[#E8EBF0] transition-colors"
        >
          <ImageDown size={11} /> {saved ? "Card saved ✓" : "Save image card"}
        </button>
      )}
      <a
        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(line)}&url=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noreferrer"
        onClick={() => track("check_shared", { symbol, mode, channel: "x" }, true)}
        data-testid="share-verdict-x"
        className="font-mono text-[11px] text-[#8B93A3] hover:text-[#E8EBF0] transition-colors"
      >
        Post on X →
      </a>
    </div>
  );
};

export const HeroCheck = ({ initialSymbol = "", navigateTo = false, fundRedirect = false, onResult }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialSymbol);
  const [result, setResult] = useState(null);
  const [pf, setPf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [examples, setExamples] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/check-examples`).then((r) => r.json()).then(setExamples).catch(() => {});
  }, []);

  useEffect(() => {
    if (result || pf) {
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    }
  }, [result, pf]);

  useEffect(() => {
    if (initialSymbol) runCheck(initialSymbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSymbol]);

  const submit = (e) => {
    e.preventDefault();
    runCheck(value);
  };

  const runCheck = async (raw) => {
    const syms = [...new Set(raw.toUpperCase().split(/[\s,;]+/).map((s) => s.replace(/[^A-Z.\-]/g, "")).filter((s) => /^[A-Z.\-]{1,6}$/.test(s)))];
    if (!syms.length) return;
    if (navigateTo && syms.length === 1) {
      track("check_used", { navigated: true }, true);
      navigate(`/check/${syms[0]}`);
      return;
    }
    setLoading(true);
    try {
      if (syms.length === 1) {
        const r = await fetch(`${API}/api/check/${syms[0]}`);
        const d = await r.json();
        if (fundRedirect && d.type === "fund") {
          navigate(`/fund/${syms[0]}`, { replace: true });
          return;
        }
        setResult(d);
        setPf(null);
        onResult?.(d);
        try { localStorage.setItem("fp_last_check", syms[0]); } catch {}
        track("check_submitted", { ticker: syms[0], signed_in: Boolean(localStorage.getItem("fp_token")) });
        track("check_used", { type: d.type || "stock", covered: d.covered }, true);
        if (d.verdict) track("check_verdict_shown", { verdict: d.verdict }, true);
        if (d.type === "fund") track("etf_xray_viewed", { symbol: syms[0] }, true);
      } else {
        const r = await fetch(`${API}/api/check/portfolio`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ symbols: syms }),
        });
        const d = await r.json();
        setPf(d);
        setResult(null);
        track("hero_portfolio_check_used", { n: syms.length, score: d.score }, true);
      }
    } catch {
      setResult(null);
      setPf(null);
      onResult?.(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md">
      <p className="text-[15px] font-semibold text-[#E8EBF0] mb-2.5" data-testid="hero-check-question">
        Early or late? Ten seconds.
      </p>
      <form onSubmit={submit} className="flex gap-2">
        <input
          data-testid="hero-check-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="NVDA, QQQ…"
          className="fp-input flex-1 min-w-0 px-4 py-3 text-sm font-mono uppercase placeholder:normal-case placeholder:font-sans"
        />
        <button
          type="submit"
          data-testid="hero-check-btn"
          disabled={loading}
          className="fp-btn px-5 py-3 text-sm whitespace-nowrap disabled:opacity-60"
        >
          {loading ? "Reading…" : "Check a ticker →"}
        </button>
      </form>
      <p className="mt-2.5 font-mono text-[11px] text-[#5A6272]">
        Any stock, ETF or fund you own. Free, no account.
      </p>
      {examples && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid="hero-check-examples">
          <span className="font-mono text-[11px] text-[#5A6272]">Try:</span>
          {[...(examples.early || []).map((s) => [s, "early"]), ...(examples.late || []).map((s) => [s, "late"]), ...(examples.funds || []).map((s) => [s, "fund"])].map(([s, kind]) => (
            <button key={s} type="button" data-testid={`example-chip-${s}`}
                    onClick={() => { setValue(s); runCheck(s); }}
                    className={`font-mono text-[11px] px-2.5 py-1 rounded-full border transition-colors hover:bg-[rgba(255,255,255,0.04)] ${kind === "late" ? "border-[rgba(199,94,94,0.4)] text-[#C75E5E]" : kind === "early" ? "border-[rgba(43,217,124,0.4)] text-[#2BD97C]" : "border-[rgba(255,255,255,0.15)] text-[#8B93A3]"}`}>
              {s}{kind === "late" ? " · late?" : kind === "early" ? " · early?" : ""}
            </button>
          ))}
        </div>
      )}
      <div ref={resultRef} />

      {pf && pf.score != null && (
        <div
          data-testid="hero-portfolio-result"
          className="mt-3 fp-card p-4"
          style={{ borderColor: `${pf.band_color}45` }}
        >
          <div className="flex items-baseline gap-3">
            <span data-testid="hero-portfolio-score" className="font-mono text-4xl font-bold" style={{ color: pf.band_color }}>
              {pf.score}
            </span>
            <span className="font-mono text-[11px] tracking-widest" style={{ color: pf.band_color }}>{pf.band}</span>
            <span className="font-mono text-[10px] text-[#5A6272] ml-auto">CROWDING SCORE · 0–100</span>
          </div>
          <p className="mt-2 text-[13px] text-[#8B93A3]">{pf.verdict}</p>
          <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1.5">
            {pf.covered.map((c) => (
              <span key={c.symbol} className="font-mono text-[11px]">
                <span className="text-[#E8EBF0] font-bold">{c.symbol}</span>{" "}
                <span style={{ color: c.crowd >= 75 ? "#C75E5E" : c.crowd >= 55 ? "#E3A44A" : "#2BD97C" }}>{c.stage}</span>
              </span>
            ))}
          </div>
          {pf.base_rate && (
            <p className="mt-2 font-mono text-[11px] text-[#E3A44A]">
              Names we flagged Crowded went a median −{Math.abs(pf.base_rate.median)}% between prints · {pf.base_rate.share_lower}% went lower · {pf.base_rate.n} receipts.
            </p>
          )}
          {pf.uncovered?.length > 0 && (
            <p className="mt-1.5 font-mono text-[11px] text-[#5A6272]">
              We read {pf.covered.length} of {pf.covered.length + pf.uncovered.length}. No specialist is building{" "}
              {pf.uncovered.map((s) => (pf.holder_counts?.[s] ? `${s} (${pf.holder_counts[s]} graded funds hold it)` : s)).join(", ")}. That's information.
            </p>
          )}
          {pf.n_theme_names > 0 && (
            <p data-testid="hero-portfolio-theme-tease" className="mt-2 text-[13px] text-[#E8EBF0]">
              {pf.n_theme_names} of your names sit inside themes the specialists are quietly building.{" "}
              <Link
                to="/register"
                onClick={() => track("signup_from_check", { from: "portfolio_themes" })}
                className="text-[#2BD97C] hover:underline"
              >
                Create a free account to see which →
              </Link>
            </p>
          )}
          {!pf.n_theme_names && (
            <p className="mt-2 text-[12px] text-[#5A6272]">
              <Link
                to="/register"
                onClick={() => track("signup_from_check", { from: "portfolio_result" })}
                className="text-[#2BD97C] hover:underline"
              >
                Create a free account
              </Link>{" "}
              to track this score weekly. We never delay a warning on your names.
            </p>
          )}
        </div>
      )}

      {pf && pf.score == null && pf.covered && (
        <div data-testid="hero-portfolio-thin" className="mt-3 fp-card p-4">
          <p className="text-[13px] text-[#8B93A3]">
            We read {pf.covered.length} of your {pf.covered.length + (pf.uncovered?.length || 0)} names. Fewer than 3
            have institutional evidence, so no honest portfolio verdict. Check them one at a time instead.
          </p>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
            {pf.covered.map((c) => (
              <span key={c.symbol} className="font-mono text-[11px]">
                <span className="text-[#E8EBF0] font-bold">{c.symbol}</span>{" "}
                <span style={{ color: c.crowd >= 75 ? "#C75E5E" : c.crowd >= 55 ? "#E3A44A" : "#2BD97C" }}>{c.stage}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {result && !result.covered && (
        <div data-testid="hero-check-uncovered" className="mt-3 fp-card p-4">
          <p className="font-mono text-lg font-bold text-[#8B93A3]">{result.verdict || "No conviction"}</p>
          <p className="mt-1.5 text-[13px] text-[#8B93A3]">{result.line}</p>
          {result.holders?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="hero-check-holders">
              {result.holders.slice(0, 3).map((h) => (
                <span key={h.fund} className="font-mono text-[11px] text-[#C9CFDA] border border-[rgba(255,255,255,0.12)] rounded px-2 py-1">
                  <span className="text-[#2BD97C]">{h.letter}</span> · {h.name} · {h.weight_pct}%
                  {h.action && <span className="text-[#8B93A7]"> · {h.action}</span>}
                </span>
              ))}
              {result.n_holders > 3 && (
                <span className="font-mono text-[11px] text-[#5A6272] px-1 py-1">+{result.n_holders - 3} more</span>
              )}
            </div>
          )}
          {result.fund_twin ? (
            <button
              type="button"
              data-testid="fund-twin-btn"
              onClick={() => { setValue(result.fund_twin); runCheck(result.fund_twin); }}
              className="mt-2.5 font-mono text-[13px] text-[#2BD97C] hover:underline"
            >
              X-ray {result.fund_twin} instead →
            </button>
          ) : (
            <p className="mt-2 text-[13px] text-[#E8EBF0]">
              <Link
                to={`/register?watch=${result.symbol}`}
                onClick={() => track("signup_from_check", { from: "uncovered" })}
                className="text-[#2BD97C] hover:underline"
              >
                Watch it, free.
              </Link>{" "}
              We'll email you the day institutional evidence appears.
            </p>
          )}
        </div>
      )}

      {result && result.covered && result.type === "index_fund" && (
        <div data-testid="hero-check-index" className="mt-3 fp-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-base font-bold">{result.symbol}</span>
            <span className="font-mono text-xl font-bold text-[#E8EBF0]">{result.verdict}</span>
          </div>
          <p className="mt-1.5 text-[13px] text-[#8B93A3]">{result.line}</p>
          <button
            type="button"
            data-testid="hero-check-index-again"
            onClick={() => { setValue(""); setResult(null); setPf(null); }}
            className="mt-2.5 font-mono text-[13px] text-[#2BD97C] hover:underline"
          >
            Check your other holdings →
          </button>
        </div>
      )}

      {result && result.covered && result.type === "fund" && (
        <div data-testid="hero-check-fund" className="mt-3 fp-card p-4 border-[rgba(227,164,74,0.3)]">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-base font-bold">{result.symbol}</span>
            <span className="font-mono text-[10px] tracking-widest text-[#5A6272]">FUND X-RAY · HOW CROWDED IS THIS FUND?</span>
          </div>
          {result.verdict && (
            <p data-testid="hero-fund-verdict" className="mt-2 font-mono text-2xl font-bold" style={{ color: result.late_pct >= 50 ? "#C75E5E" : result.late_pct >= 25 ? "#E3A44A" : "#2BD97C" }}>
              {result.late_pct}% of the fund by weight sits in late-stage names
            </p>
          )}
          <p className="mt-1.5 font-mono text-[12px] text-[#8B93A3]">
            {Object.entries(result.stage_pct).map(([st, w]) => `${w}% ${st}`).join(" · ")}
            {result.uncovered_pct > 0 ? ` · ${result.uncovered_pct}% not covered` : ""}
          </p>
          {result.crowded_names?.length > 0 && (
            <p className="mt-2 font-mono text-[12px] text-[#E3A44A]">
              The crowded weight: {result.crowded_names.slice(0, 5).map((c) => `${c.symbol} ${c.weight}%`).join(" · ")}{result.crowded_names.length > 5 ? " …" : ""}
            </p>
          )}
          <p className="mt-2 text-[12px] text-[#5A6272]">{result.line}</p>
          {result.base_rate_line && (
            <p className="mt-2 font-mono text-[11px] text-[#E3A44A]">{result.base_rate_line}</p>
          )}
          <p className="mt-2.5 text-[13px] text-[#E8EBF0]">
            <Link
              to="/register"
              onClick={() => track("signup_from_check", { from: "fund_xray" })}
              className="text-[#2BD97C] hover:underline"
            >
              Watch this fund
            </Link>{" "}
            and we'll tell you when it crosses 50% crowded.
          </p>
          <ShareRead
            symbol={result.symbol}
            mode="fund"
            card={result}
            line={`How crowded is ${result.symbol}? ${result.late_pct}% of it by weight sits in late-stage names.`}
          />
        </div>
      )}

      {result && result.covered && result.type !== "fund" && (
        <div
          data-testid="hero-check-result"
          className={`mt-3 fp-card p-4 ${result.warning ? "border-[rgba(227,164,74,0.35)]" : "border-[rgba(43,217,124,0.25)]"}`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-base font-bold">{result.symbol}</span>
            <span
              data-testid="hero-check-verdict"
              className="font-mono text-xl font-bold"
              style={{ color: TONE_COLORS[result.verdict_tone] || (result.verdict === "Late" ? "#C75E5E" : result.verdict === "Well positioned" ? "#E3A44A" : "#2BD97C") }}
            >
              {result.verdict}
            </span>
          </div>
          {result.share_class_note && (
            <p data-testid="share-class-note" className="mt-1.5 font-mono text-[11px] text-[#E3A44A]">{result.share_class_note}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <StageBadge stage={result.stage} />
            <span className="font-mono text-[11px]" style={{ color: TAPE_COLORS[result.tape] || "#8B93A3" }}>
              OPTIONS {TAPE_LABELS[result.tape]?.toUpperCase() || "—"}
            </span>
            {result.n_specialists != null && (
              <span className="font-mono text-[11px] text-[#8B93A3]">{result.n_specialists} proven specialists</span>
            )}
            {result.stage_age_days != null && (
              <span className="font-mono text-[11px] text-[#5A6272]">
                {result.stage_age_days === 0 ? "since today" : `since ${result.last_change}`}
              </span>
            )}
          </div>
          {result.caution_line && (
            <p data-testid="hero-check-caution" className="mt-2 text-[13px] text-[#E3A44A]">
              {result.caution_line}
            </p>
          )}
          {result.meaning_line && (
            <p data-testid="hero-check-meaning" className="mt-2 text-[13px] text-[#E8EBF0]">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#5A6272] mr-2">What this means:</span>
              {result.meaning_line}
            </p>
          )}
          {result.exits_info?.n > 0 && (
            <p data-testid="hero-check-exits" className="mt-2 font-mono text-[12px] font-bold text-[#C75E5E]">
              {result.exits_info.n === 1 ? "A specialist" : `${result.exits_info.n} specialists`} walked
              away from this trade in the last {result.exits_info.window}
              {result.exits_info.top_success_rate > 0 && (
                <>
                  . The best of them runs a {result.exits_info.top_success_rate}% success rate
                  {result.exits_info.top_held_since && ` and had held since ${result.exits_info.top_held_since}`}
                </>
              )}
              . At this stage, the exits are the signal.
            </p>
          )}
          {result.n_specialists != null && (
            <p data-testid="hero-check-specialist-note" className="mt-2 text-[11px] text-[#5A6272]">
              Specialists are the top-ranked pickers in this exact niche, graded on how their
              disclosed buys performed. Not generalist whales.
            </p>
          )}
          {result.base_rate_line && (
            <p data-testid="hero-check-base-rate" className="mt-2.5 font-mono text-[11px] text-[#E3A44A]">
              {result.base_rate_line}
            </p>
          )}
          {result.theme_line && (
            <p data-testid="hero-check-theme-line" className="mt-2 text-[13px] text-[#E8EBF0]">{result.theme_line}</p>
          )}
          {result.what_would_change && (
            <p data-testid="hero-check-would-change" className="mt-2 text-[12px] text-[#8B93A3]">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#5A6272] mr-2">What would change this read:</span>
              {result.what_would_change}
            </p>
          )}
          {result.twin && result.twin.kind === "warning" && (
            <a
              href="#receipts"
              data-testid="hero-check-twin-warning"
              className="mt-2.5 block font-mono text-[12px] text-[#E3A44A] hover:underline"
            >
              Across the last {result.twin.n} names we flagged in this state: −
              {Math.abs(result.twin.avg_pct)}% on average in the weeks after. See the receipts →
            </a>
          )}
          {result.twin && result.twin.kind === "find" && (
            <a
              href="#receipts"
              data-testid="hero-check-twin-find"
              className="mt-2.5 block font-mono text-[12px] text-[#2BD97C] hover:underline"
            >
              Across the last {result.twin.n} names we caught this early: +
              {Math.abs(result.twin.avg_pct)}% on average before the crowd arrived. See the receipts →
            </a>
          )}
          <p className="mt-2.5 text-[12px] text-[#5A6272]">
            <Link
              to={`/register?watch=${result.symbol}`}
              data-testid="check-save-cta"
              onClick={() => track("signup_from_check", { from: "result" })}
              className="text-[#2BD97C] hover:underline font-semibold"
            >
              Save this and check your whole portfolio, free.
            </Link>{" "}
            We never delay a warning on your names.
          </p>
          <ShareRead
            symbol={result.symbol}
            card={result}
            line={`Is it too late to buy ${result.symbol}? The specialists' read: ${result.verdict}.`}
          />
        </div>
      )}
    </div>
  );
};

export default HeroCheck;
